import PropTypes from "prop-types";
import React, { useState, useCallback, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";
import ReactQuill from "react-quill";

import "react-quill/dist/quill.snow.css";
import "./RichTextEditor.scss";

const messages = defineMessages({
  enterUrl: {
    id: "components.richTextEditor.enterUrl",
    description: "Enter video URL",
    defaultMessage:
      "Enter video URL (e.g. https://www.youtube.com/watch?v=xxx)",
  },
});

const RichTextEditor = ({ value, onChange }) => {
  const intl = useIntl();
  const quillRef = useRef(null);
  const [content, setContent] = useState(value || "");

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = () => {
      const file = input.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const imageUrl = reader.result;
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);
        editor.insertEmbed(range.index, "image", imageUrl, "user");
      };
      reader.readAsDataURL(file);
    };
  }, []);

  // Modified video handler: prompts for a video URL and converts YouTube URLs.
  const videoHandler = useCallback(() => {
    const url = prompt(intl.formatMessage(messages.enterUrl));
    if (url) {
      let embedUrl = url;
      // Convert standard YouTube watch URL to embed URL.
      const youtubeRegex =
        /^(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/;
      const match = url.match(youtubeRegex);
      if (match && match[1]) {
        embedUrl = `https://www.youtube.com/embed/${match[1]}`;
      }
      // Convert short YouTube URL to embed URL.
      const youtuBeRegex = /^(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^?]+)/;
      const match2 = url.match(youtuBeRegex);
      if (match2 && match2[1]) {
        embedUrl = `https://www.youtube.com/embed/${match2[1]}`;
      }
      const editor = quillRef.current.getEditor();
      const range = editor.getSelection(true);
      editor.insertEmbed(range.index, "video", embedUrl, "user");
    }
  }, []);

  // Define toolbar configuration as an object with container and custom handlers.
  const modules = {
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "clean"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image", "video"],
      ],
      handlers: {
        image: imageHandler,
        video: videoHandler,
      },
    },
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "video",
    "color",
    "clean",
  ];

  const handleChange = (newContent) => {
    setContent(newContent);
    if (onChange) onChange(newContent);
  };

  return (
    <div className="quill-container">
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={content}
        formats={formats}
        modules={modules}
        onChange={handleChange}
      />
    </div>
  );
};

RichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export default RichTextEditor;
